import { useCallback, useState } from "react";

import { downloadFromURL } from "@/utils/download";

export type DownloadableFile = {
  __typename: "File";
  url: string;
  name: string;
};

export type DownloadableAsset = {
  __typename: "Asset";
  previewUrl?: string | null;
  name: string;
};

export type DownloadableObject = DownloadableFile | DownloadableAsset;

const Formatters = {
  file: (file: DownloadableFile) => {
    return {
      name: file.name,
      url: file.url,
    };
  },
  asset: (asset: DownloadableAsset, index: number) => {
    let assetName = asset.name?.trim() || `asset-${index}.png`;

    if (!assetName.toLocaleLowerCase().endsWith(".png")) {
      assetName = `${assetName}.png`;
    }

    return {
      name: assetName,
      // The preview URL should always be up-to-date, so we can use it instead of compiling the image from the Canvas directly.
      url: asset.previewUrl,
    };
  },
};

export const useDownloadObjects = () => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadObjects = useCallback(
    async (
      downloadableObjects: DownloadableObject[],
      { zipName = "files.zip" }: { zipName?: string } = {},
    ) => {
      const downloadableFiles = downloadableObjects
        .map((downloadableObject, index) => {
          if (downloadableObject.__typename === "File") {
            return Formatters.file(downloadableObject);
          }

          return Formatters.asset(downloadableObject, index);
        })
        .filter((file): file is { name: string; url: string } => !!file.url);

      try {
        setIsDownloading(true);
        await downloadFromURL(downloadableFiles, { zipName });
      } catch (error) {
        console.error("Error downloading objects:", error);
      } finally {
        setIsDownloading(false);
      }
    },
    [],
  );

  return {
    isDownloading,
    downloadObjects,
  };
};
